import type {QuoteDisplayStatus} from 'nutshell-graphql-types';
import type {
    // has formmatedDiscount, price = MoneyAmount
    GetQuoteById_quote_productLineItems_edges_node as ProductLineItem,
    UpdateSalesDocumentStatus_salesDocumentUpdateStatus_salesDocument_Invoice_total as Total,
    ProductLineItemInput,
    GetQuoteById_quote_subtotalLineItems_edges_node as QuoteSubtotalLineItem,
    QuoteSubtotalItemsUpdateInput,
    GetQuoteById_quote_subtotalLineItems_edges_node as QuoteSubtotalLineItemType,
} from 'nutshell-graphql-types';

import {
    SendIcon,
    CopyIcon,
    ArchiveIcon,
    MinusCircleIcon,
    InvoiceIcon,
    TimesCircleIcon,
} from '../icon';
import type {SalesDocumentType} from '../lead-sidebar/sales-documents/types';

import {OPERATION_MAP, MODIFIER_UNITS} from './wrappers/quote-subtotal-wrapper';

export const HEADER = 'header';
export const PREPARED_FOR = 'prepared for';
export const QUOTE_DETAILS = 'details';
export const PRODUCT_TABLE = 'products';
export const PRODUCT_TABLE_TEMPLATE = 'customize your columns';
export const NOTES = 'notes';
export const SUBTOTAL_TABLE_TEMPLATE = 'subtotal';
export const SUBTOTAL_TABLE = 'pricing';
export const FOOTER = 'footer';
export const PAYMENT_INFO = 'payment methods & terms';
export const NO_SECTION_NAME = '';

export type SectionName =
    | typeof HEADER
    | typeof PREPARED_FOR
    | typeof QUOTE_DETAILS
    | typeof PRODUCT_TABLE
    | typeof PRODUCT_TABLE_TEMPLATE
    | typeof NOTES
    | typeof SUBTOTAL_TABLE_TEMPLATE
    | typeof SUBTOTAL_TABLE
    | typeof FOOTER
    | typeof NO_SECTION_NAME;

export type FieldError = {
    message: string;
    field: string;
};

export const TEMP_NEW_PRODUCT_ID = 'New-Item-Id';
export const TEMP_NEW_SUBTOTAL_ID = 'New-Subtotal-Id';

// Fallbacks for when a product item is saved to the backend without a value
const PRODUCT_LINE_ITEM_FALLBACKS = {
    name: 'Product',
    quantity: 1,
    price: '0',
    discountUnit: 'FIXED_AMOUNT',
    discountNumber: '0',
} as const;

// Fallbacks for when a subtotal item is saved to the backend without a value
const SUBTOTAL_LINE_ITEM_FALLBACKS = {
    label: 'Fee',
    value: '0',
} as const;

// This is a helper function to pull what we need from the product line item
// and return it in the format of the product line item input typef
export const generateProductLineItemInputType = (
    product: ProductLineItem
): ProductLineItemInput => {
    return {
        id: product.id.includes(TEMP_NEW_PRODUCT_ID) ? null : product.id,
        productId: product.product && product.product.id,
        name: product.name || PRODUCT_LINE_ITEM_FALLBACKS.name,
        description: product.description,
        sku: product.sku,
        quantity: product.quantity || PRODUCT_LINE_ITEM_FALLBACKS.quantity,
        price: product.price.amount.toString() || PRODUCT_LINE_ITEM_FALLBACKS.price,
        term: product.term,
        discountUnit:
            !product.discountNumber || Math.abs(product.discountNumber) < Number.EPSILON
                ? null
                : product.discountUnit || PRODUCT_LINE_ITEM_FALLBACKS.discountUnit,
        discountNumber:
            !product.discountNumber || Math.abs(product.discountNumber) < Number.EPSILON
                ? null
                : product.discountNumber.toString() || PRODUCT_LINE_ITEM_FALLBACKS.discountNumber,
    };
};

// Need to convert the subtotal line items to the format that the backend
// expects, specifically, our temporary IDs for new rows need to be set to
// null so the backend knows this is a new row and creates an id for it.
export const generateSubtotalLineItemsUpdateInput = (
    subtotalLineItems: QuoteSubtotalLineItem[],
    quoteId: string
): QuoteSubtotalItemsUpdateInput => {
    const lineItems = subtotalLineItems.map((item) => ({
        id: item.id.includes(TEMP_NEW_SUBTOTAL_ID) ? null : item.id,
        label: item.label || SUBTOTAL_LINE_ITEM_FALLBACKS.label,
        modifierUnit: item.modifierUnit,
        modifierValue: item.modifierValue.toString() || SUBTOTAL_LINE_ITEM_FALLBACKS.value,
        operation: item.operation,
    }));

    return {
        id: quoteId,
        lineItems: lineItems,
    };
};

export function calculateTotal(subtotalLineItems: QuoteSubtotalLineItemType[], subtotal: number) {
    return subtotalLineItems.reduce((acc, item) => {
        const amount =
            item.operation === OPERATION_MAP.SUBTRACT ? -item.price.amount : item.price.amount;

        return acc + amount;
    }, subtotal);
}

export function generateSubtotalLineitemDefaults(
    total: Total,
    idCounterRef: React.RefObject<number>
) {
    return {
        __typename: 'SubtotalLineItem',
        // @ts-expect-error
        id: `${TEMP_NEW_SUBTOTAL_ID}-${idCounterRef.current++}`,
        formattedValue: '$0.00',
        label: 'Shipping',
        modifierUnit: MODIFIER_UNITS.FIXED_AMOUNT,
        modifierValue: 1,
        price: {
            __typename: 'Money',
            amount: 1.0,
            currency: total.currency,
            formatted: `${total.symbol}1`,
            formattedWithCents: `${total.symbol}1.00`,
            symbol: total.symbol,
        },
        operation: OPERATION_MAP.ADD,
    };
}

export const getIsSectionImmutable = (displayStatus: QuoteDisplayStatus): boolean => {
    return displayStatus !== 'DRAFT';
};

/*
 * This function makes a deep copy of a ProductLineItem object
 * and sets the amount of the price correctly this is to
 * prevent the nested object from being lost when the object is copied
 */
export const makeDeepProductLineItemCopy = (item: ProductLineItem) => {
    return {...item, price: {...item.price, amount: item.price.amount}};
};

export const getPrimaryActionButtonText = (
    displayStatus: QuoteDisplayStatus,
    type: SalesDocumentType
): string => {
    switch (displayStatus) {
        case 'DRAFT':
            return 'Mark as ready';
        case 'PUBLISHED':
            return type === 'Quote' ? 'Send quote' : 'Send invoice';
        case 'SENT':
            return type === 'Quote' ? 'Mark as accepted' : 'Mark as paid';
        default:
            return '';
    }
};

export const getNextAdvanceableStatus = (displayStatus: QuoteDisplayStatus): QuoteDisplayStatus => {
    switch (displayStatus) {
        case 'DRAFT':
            return 'PUBLISHED';
        case 'PUBLISHED':
            return 'SENT';
        case 'SENT':
            return 'ACCEPTED';
        default:
            return displayStatus;
    }
};

export const getIsPrimaryActionVisible = (displayStatus: QuoteDisplayStatus): boolean => {
    return displayStatus === 'DRAFT' || displayStatus === 'PUBLISHED' || displayStatus === 'SENT';
};

export const getQuoteActionMenuOptions = (
    quoteId: string,
    displayStatus: QuoteDisplayStatus,
    sentTime: string | null | undefined,
    setIsArchivingPopoverOpen: (isOpen: boolean) => void,
    duplicateQuote: (id: string) => void,
    updateQuoteStatus: (status: QuoteDisplayStatus) => void,
    createInvoiceFromQuote: (id: string) => void
) => {
    const actionMenuOptions = [
        {
            icon: <CopyIcon size={14} />,
            label: 'Make a copy',
            handleSelect: () => {
                duplicateQuote(quoteId);
            },
        },
    ];

    if (displayStatus === 'PUBLISHED' && sentTime === null) {
        actionMenuOptions.push({
            icon: <SendIcon size={14} />,
            label: 'Mark as sent',
            handleSelect: () => {
                updateQuoteStatus('SENT');
            },
        });
    }
    if (displayStatus === 'PUBLISHED') {
        actionMenuOptions.push({
            icon: <MinusCircleIcon size={14} />,
            label: 'Revoke',
            handleSelect: () => {
                updateQuoteStatus('REVOKED');
            },
        });
    } else if (displayStatus === 'DRAFT' || displayStatus === 'REVOKED') {
        actionMenuOptions.push({
            icon: <ArchiveIcon size={14} />,
            label: 'Remove',
            handleSelect: () => {
                setIsArchivingPopoverOpen(true);
            },
        });
    }
    if (displayStatus === 'ACCEPTED') {
        actionMenuOptions.push({
            icon: <InvoiceIcon size={14} />,
            label: 'Create an invoice',
            handleSelect: () => {
                createInvoiceFromQuote(quoteId);
            },
        });
    }

    return actionMenuOptions;
};

export const getInvoiceActionMenuOptions = (
    quoteId: string,
    displayStatus: QuoteDisplayStatus,
    setIsArchivingPopoverOpen: (isOpen: boolean) => void,
    duplicateInvoice: (id: string) => void,
    updateInvoiceStatus: (status: QuoteDisplayStatus) => void
) => {
    const actionMenuOptions = [
        {
            icon: <CopyIcon size={14} />,
            label: 'Make a copy',
            handleSelect: () => {
                duplicateInvoice(quoteId);
            },
        },
    ];
    if (displayStatus === 'PUBLISHED' || displayStatus === 'SENT') {
        actionMenuOptions.push({
            icon: <TimesCircleIcon size={14} />,
            label: 'Void',
            handleSelect: () => {
                updateInvoiceStatus('REVOKED');
            },
        });
    } else if (displayStatus === 'DRAFT' || displayStatus === 'REVOKED') {
        actionMenuOptions.push({
            icon: <ArchiveIcon size={14} />,
            label: 'Remove',
            handleSelect: () => {
                setIsArchivingPopoverOpen(true);
            },
        });
    }

    return actionMenuOptions;
};
